import { cn } from "@lib/cn";
import { useSlidesLibraryStoreShallow } from "@modules/slides-library/shared";
import { useReorderSlidesFoldersMutation } from "@/shared/services/slides-folder";
import { useEffect, useState } from "react";
import type { TFolderWithChildren } from "@/shared/types";
import { IconChevronRight, IconFolder, IconGripVertical } from "@tabler/icons-react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FolderActions } from "./folder-actions";

export const RecursiveSidebarFolder = ({
  folder,
  level = 0,
  isDragging,
  dragHandleProps,
}: {
  folder: TFolderWithChildren;
  level?: number;
  isDragging?: boolean;
  dragHandleProps?: Record<string, unknown>;
}) => {
  const { search, setFolderId, folderId } = useSlidesLibraryStoreShallow(
    (s) => ({
      search: s.search,
      setFolderId: s.setFolderId,
      folderId: s.folderId,
    }),
  );
  const isActive = folderId === folder._id;
  const [isOpen, setIsOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const paddingLeft = 24 + level * 16;
  const hasChildren = folder.children.length > 0;

  const handleRowClick = () => {
    setFolderId(folder._id);
  };

  return (
    <div className={cn(isDragging && "tw-opacity-50")}>
      <div
        onClick={handleRowClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleRowClick();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={folder.title}
        aria-pressed={isActive && !search}
        className={cn(
          "tw-group/folder tw-relative tw-mx-1 tw-flex tw-h-8 tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-md tw-pr-1 tw-font-sans tw-text-[14px] tw-font-normal tw-text-blue tw-transition-colors hover:tw-bg-blue-100 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-blue-200",
          (isActive && !search) || isMenuOpen ? "tw-bg-blue-100" : "",
        )}
        style={{ paddingLeft: `${paddingLeft}px` }}
      >
        {/* Drag handle */}
        {dragHandleProps && (
          <span
            {...dragHandleProps}
            className={cn(
              "tw-absolute tw-left-0.5 tw-top-1/2 tw-invisible tw-cursor-grab -tw-translate-y-1/2 group-hover/folder:tw-visible active:tw-cursor-grabbing",
              (isActive && !search) && "tw-visible",
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <IconGripVertical
              className="tw-size-3 tw-text-blue-500"
              stroke={1}
            />
          </span>
        )}

        <IconFolder className="tw-size-4 tw-shrink-0 tw-text-blue" stroke={1} />

        <span className="tw-min-w-0 tw-flex-1 tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap">
          {folder.title}
        </span>

        {/* Right actions — dots + chevron */}
        <div
          className={cn(
            "tw-invisible tw-flex tw-items-center tw-gap-0.5 group-hover/folder:tw-visible group-focus-within/folder:tw-visible",
            (isMenuOpen || (isActive && !search)) && "tw-visible",
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <FolderActions isOpen={isMenuOpen} setIsOpen={setIsMenuOpen} folder={folder} />
          {hasChildren && (
            <button
              type="button"
              className="tw-flex tw-size-6 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-none tw-bg-transparent tw-p-0 tw-outline-none"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(!isOpen);
              }}
            >
              <IconChevronRight
                className={cn(
                  "tw-size-3.5 tw-text-blue tw-opacity-50 tw-transition-transform",
                  isOpen && "tw-rotate-90",
                )}
                stroke={1.5}
              />
            </button>
          )}
        </div>
      </div>

      {hasChildren && (
        <div
          className={cn(
            "tw-grid tw-transition-[grid-template-rows] tw-duration-200",
            {
              "[grid-template-rows:1fr]": isOpen,
              "[grid-template-rows:0fr]": !isOpen,
            },
          )}
        >
          <div className="tw-overflow-hidden">
            <div className="tw-mt-0.5">
              <SortableChildren
                folders={folder.children as TFolderWithChildren[]}
                level={level + 1}
                parentId={folder._id}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const SortableChildren = ({
  folders,
  level,
  parentId,
}: {
  folders: TFolderWithChildren[];
  level: number;
  parentId: string;
}) => {
  const { mutate: reorderFolders } = useReorderSlidesFoldersMutation();
  const [localFolders, setLocalFolders] = useState(folders);

  useEffect(() => {
    setLocalFolders(folders);
  }, [folders]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = localFolders.findIndex((f) => f._id === active.id);
      const newIndex = localFolders.findIndex((f) => f._id === over.id);
      const newOrder = arrayMove(localFolders, oldIndex, newIndex);
      setLocalFolders(newOrder);
      reorderFolders({ parentId, folderIds: newOrder.map((f) => f._id) });
    }
  };

  if (localFolders.length <= 1) {
    return (
      <>
        {localFolders.map((child) => (
          <RecursiveSidebarFolder key={child._id} folder={child} level={level} />
        ))}
      </>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext
        items={localFolders.map((f) => f._id)}
        strategy={verticalListSortingStrategy}
      >
        {localFolders.map((child) => (
          <SortableChildFolder key={child._id} folder={child} level={level} />
        ))}
      </SortableContext>
    </DndContext>
  );
};

const SortableChildFolder = ({
  folder,
  level,
}: {
  folder: TFolderWithChildren;
  level: number;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: folder._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <RecursiveSidebarFolder
        folder={folder}
        level={level}
        isDragging={isDragging}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
};
